// Thin API client.
// Two auth modes:
//   - Firebase enabled (prod): sends `Authorization: Bearer <Firebase ID token>`.
//     The token auto-refreshes via the Firebase SDK.
//   - Stub (dev): sends `X-Stub-Player` from localStorage, no Firebase needed.

import type { State, Viewer, MyPick } from "./state";
import { firebaseEnabled, getIdToken } from "./firebase";

const STORAGE_KEY = "wc2026.stubPlayer";

export function getStubPlayer(): string | null {
  return localStorage.getItem(STORAGE_KEY);
}

export function setStubPlayer(id: string | null) {
  if (id) localStorage.setItem(STORAGE_KEY, id);
  else localStorage.removeItem(STORAGE_KEY);
}

async function headers(): Promise<Record<string, string>> {
  const h: Record<string, string> = { "Content-Type": "application/json" };
  if (firebaseEnabled()) {
    const token = await getIdToken();
    if (token) h["Authorization"] = `Bearer ${token}`;
  } else {
    const id = getStubPlayer();
    if (id) h["X-Stub-Player"] = id;
  }
  return h;
}

export async function fetchState(): Promise<State> {
  const r = await fetch("/api/state", { headers: await headers() });
  if (!r.ok) throw new Error(`/api/state ${r.status}`);
  return r.json();
}

export async function fetchMe(): Promise<Viewer | null> {
  const h = await headers();
  // No identity info attached → don't even ask
  if (!h["Authorization"] && !h["X-Stub-Player"]) return null;
  const r = await fetch("/api/me", { headers: h });
  if (r.status === 401) return null;
  if (!r.ok) throw new Error(`/api/me ${r.status}`);
  return r.json();
}

export async function fetchMyPicks(): Promise<Record<string, MyPick>> {
  const r = await fetch("/api/my-picks", { headers: await headers() });
  if (r.status === 401) return {};
  if (!r.ok) throw new Error(`/api/my-picks ${r.status}`);
  return r.json();
}

export async function acknowledgeRule(rule_id: string): Promise<void> {
  const r = await fetch("/api/rules/acknowledge", {
    method: "POST",
    headers: await headers(),
    body: JSON.stringify({ rule_id }),
  });
  if (!r.ok) {
    const body = await r.json().catch(() => ({}));
    throw new Error(body.error || `acknowledgement failed ${r.status}`);
  }
}

export async function submitPrediction(
  match_id: string, home_score: number, away_score: number, predicted_winner: "home" | "away",
): Promise<void> {
  const r = await fetch("/api/predictions", {
    method: "POST",
    headers: await headers(),
    body: JSON.stringify({ match_id, home_score, away_score, predicted_winner }),
  });
  if (!r.ok) {
    const body = await r.json().catch(() => ({}));
    if (r.status === 428) window.dispatchEvent(new Event("wc2026:rules-pending"));
    throw new Error(body.error || `submit failed ${r.status}`);
  }
}
